import Attrs from '../models/Attrs'

import type  { BaseBuilderVersion, Flow, UserData } from '../models/types'

export function testModeIsOn(url: URL) {
  if (url.searchParams.get('savvy_test') === 'false') return false
  if (url.searchParams.get('savvy_test')) return true

  //* If no savvy_test param is provided, use the default behavior
  return testModeIsDefaultOn(url)
}

function testModeIsDefaultOn(url: URL) {
  return env(url) !== 'production'
}

export function env(url: URL) {
  const h = url.hostname
  const forcedEnv = url.searchParams.get('savvy_env')
  if (h.startsWith('localhost')) return 'local'
  if (h.endsWith('cidemo.co') || h.endsWith('branch.trysavvy.com')) return 'ci'
  if (h.endsWith('csb.app')) return 'branch'
  if (
    [
      'preview-dev.trysavvy.com',
      'preview-dev.heysavvy.workers.dev',
      'preview-dev.embeddables.com',
    ].includes(h)
  ) {
    return 'preview-dev'
  }
  if (
    [
      'preview-staging.trysavvy.com',
      'preview-staging.heysavvy.workers.dev',
      'preview-staging.embeddables.com',
    ].includes(h)
  ) {
    return 'preview-staging'
  }
  if (['preview-ready-for-qa.trysavvy.com', 'preview-ready-for-qa.embeddables.com'].includes(h)) {
    return 'preview-ready-for-qa'
  }
  if (
    [
      'preview-side-staging.trysavvy.com',
      'preview-side-staging.heysavvy.workers.dev',
      'preview-side-staging.embeddables.com',
    ].includes(h)
  ) {
    return 'preview-side-staging'
  }
  if (
    ['preview-side-staging-2.trysavvy.com', 'preview-side-staging-2.embeddables.com'].includes(h)
  ) {
    return 'preview-side-staging-2'
  }
  if (
    ['preview-side-staging-3.trysavvy.com', 'preview-side-staging-3.embeddables.com'].includes(h)
  ) {
    return 'preview-side-staging-3'
  }
  if (
    [
      'preview.trysavvy.com',
      'preview.heysavvy.workers.dev',
      'stimulus-1-indexhtml.heysavvy.workers.dev',
      'preview.embeddables.com',
      'app.embeddables.com',
    ].includes(h) ||
    url.href.startsWith('https://app.embeddables.com/edit/')
  )
    return 'preview'
  if (forcedEnv === 'dev') return 'dev'
  if (forcedEnv === 'ci') return 'ci'
  if (forcedEnv === 'staging') return 'staging'
  if (forcedEnv === 'side-staging') return 'side-staging'
  if (forcedEnv === 'side-staging-2') return 'side-staging-2'
  return 'production'
}

export function redactSecret(secret: string) {
  if (!secret) return '<NO SECRET>'

  // If secret is <16 chars, return everything except the first and last 2 characters
  if (secret.length < 16) {
    return secret.slice(0, 2) + '*'.repeat(secret.length - 4) + secret.slice(-2)
  }

  // If secret is less than 32 chars, return everything except the first and last 4 characters
  if (secret.length < 32) {
    return secret.slice(0, 4) + '*'.repeat(secret.length - 8) + secret.slice(-4)
  }

  // Redact everything except the first and last 8 characters
  return secret.slice(0, 8) + '*'.repeat(secret.length - 16) + secret.slice(-8)
}

export function getCurrencySymbol(currency?: string) {
  switch (currency) {
    case 'gbp': {
      return '£'
    }
    case 'eur': {
      return '€'
    }
    case 'usd':
    case 'cad':
    default: {
      return '$'
    }
  }
}

//todo - consider combining somehow with computedValues.js
export function computeAllTemplateValues(value: any, userData: UserData): any {
  switch (typeof value) {
    case 'string': {
      // If value is of the format {{key}} then replace with the value from userData
      const valueTrimmed = value.trim()
      if (valueTrimmed.startsWith('{{') && valueTrimmed.endsWith('}}')) {
        const key = valueTrimmed.slice(2, -2)
        return userData[key] || undefined
      }
      return value
    }
    case 'object': {
      if (Array.isArray(value)) {
        return value.map((_item: any) => computeAllTemplateValues(_item, userData))
      }
      return Object.entries(value).reduce((acc: Record<string, any>, [key, value]) => {
        acc[key] = computeAllTemplateValues(value, userData)
        return acc
      }, {})
    }
    default:
      return value
  }
}

export function getBaseBuilderVersion(
  builder_version: Flow['builder_version']
): BaseBuilderVersion {
  return builder_version ? (Math.floor(builder_version) as BaseBuilderVersion) : 3
}

export function addLanguageController({
  attrs,
  component,
  keys,
  defaultValue,
  mapKeys = {},
}: {
  attrs: Attrs
  component: Record<string, any>
  keys: string[]
  defaultValue?: string
  mapKeys?: Record<string, string>
}) {
  //* Check if the component has any text attributes for different languages
  const hasVariants = Object.keys(component).some(
    (key) => key.startsWith('lang-') && keys.some((k) => key.endsWith(`-${k}`))
  )

  if (!hasVariants) return

  //* Add language controller
  attrs.addController('language')

  //* Add values for the language controller
  for (const type of keys) {
    const baseValue = (defaultValue ?? (component as any)[type] ?? '').replace(/"/g, '&quot;')
    attrs.addValueIfExists('language', mapKeys[type] ?? type, baseValue)
  }

  //* Add values for the language controller (it will be the fallback for the language controller)
  Object.keys(component).forEach((key) => {
    if (
      key.startsWith('lang-') &&
      keys.some((k) => key.endsWith(`-${k}`)) &&
      typeof (component as any)[key] === 'string'
    ) {
      const value = (component as any)[key].replace(/"/g, '&quot;')
      const [prefix, language, type] = key.split('--')
      attrs.add(`${prefix}--${language}--${mapKeys[type] ?? type}`, value)
    }
  })
}
